"use client"

import type React from "react"
import { useEffect } from "react"
import { Search, X } from "lucide-react"
import { Input } from "@/components/ui/input"
import { stateAbbrevToName } from "../utils/stateMappings"
import { countryNameToAbbrev, countryAbbrevToName } from "../utils/countryMappings"
import { useState, useCallback } from "react"
import { stateNameToAbbrev } from "../utils/stateMappings"
import AssignTerritoryModal from "./AssignTerritoryModal"

interface Territory {
  id: number
  name: string
  territories: string[]
  color: string
}

interface TerritorySearchProps {
  territories: Territory[]
  onFilteredTerritoriesChange: (territories: Territory[]) => void
  mapMode: "us" | "world"
  allRegions: string[]
  onHighlightRegions: (regions: string[]) => void
  onAssignRegion: (region: string, territoryId: number) => void
  onSearchHighlightRegions: (regions: string[]) => void // Add this new prop
  onFilterChange: (filter: string) => void
}

const TerritorySearch: React.FC<TerritorySearchProps> = ({
  territories,
  onFilteredTerritoriesChange,
  mapMode,
  allRegions,
  onHighlightRegions,
  onAssignRegion,
  onFilterChange,
  onSearchHighlightRegions, // Add this new prop
}) => {
  const [searchTerm, setSearchTerm] = useState("")
  const [activeFilter, setActiveFilter] = useState("all")
  const [unassignedRegions, setUnassignedRegions] = useState<string[]>([])
  const [matchingRegions, setMatchingRegions] = useState<string[]>([])
  const [assignModalOpen, setAssignModalOpen] = useState(false)
  const [selectedRegion, setSelectedRegion] = useState<string | null>(null)

  const getRegionName = useCallback(
    (region: string) => {
      return mapMode === "us" ? stateAbbrevToName(region) : countryAbbrevToName(region)
    },
    [mapMode],
  )

  const getRegionAbbrev = useCallback(
    (name: string) => {
      return mapMode === "us" ? stateNameToAbbrev(name) : countryNameToAbbrev(name)
    },
    [mapMode],
  )

  useEffect(() => {
    // Find all assigned regions
    const assignedRegions = new Set(territories.flatMap((t) => t.territories))

    // Calculate unassigned regions
    const unassigned = allRegions.filter((region) => !assignedRegions.has(region))
    setUnassignedRegions(unassigned)

    // Find matching regions based on search term
    const matching = searchTerm
      ? allRegions.filter((region) => {
          const regionName = getRegionName(region).toLowerCase()
          const searchLower = searchTerm.toLowerCase()
          return region.toLowerCase().includes(searchLower) || regionName.includes(searchLower)
        })
      : []
    setMatchingRegions(matching)

    // Highlight regions based on search term and active filter
    if (searchTerm) {
      onSearchHighlightRegions(matching)
      onHighlightRegions([])
    } else if (activeFilter === "unassigned") {
      onHighlightRegions(unassigned)
      onSearchHighlightRegions([])
    } else {
      onHighlightRegions([])
      onSearchHighlightRegions([])
    }

    // Filter territories for display
    const filtered = territories.filter((territory) => {
      const matchesSearch =
        territory.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        territory.territories.some((t) => {
          const regionName = getRegionName(t).toLowerCase()
          const searchLower = searchTerm.toLowerCase()
          return t.toLowerCase().includes(searchLower) || regionName.includes(searchLower)
        })

      if (activeFilter === "unassigned") {
        return matchesSearch && territory.territories.length === 0
      } else {
        return matchesSearch
      }
    })

    onFilteredTerritoriesChange(filtered)
  }, [
    searchTerm,
    activeFilter,
    territories,
    allRegions,
    onFilteredTerritoriesChange,
    onHighlightRegions,
    getRegionName,
    onSearchHighlightRegions, // Add this dependency
  ])

  const handleRegionClick = (region: string) => {
    setSelectedRegion(region)
    setAssignModalOpen(true)
  }

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <div className="relative">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-500" />
          <Input
            type="text"
            placeholder="Search regions..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-8 pr-8 w-full"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm("")}
              className="absolute right-2 top-2.5 text-gray-500 hover:text-gray-700"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <div className="flex gap-2 text-sm">
          <button
            onClick={() => {
              setActiveFilter("all")
              onFilterChange("all")
            }}
            className={`px-2 py-1 rounded-full transition-colors ${
              activeFilter === "all" ? "bg-blue-100 text-blue-700" : "hover:bg-gray-100"
            }`}
          >
            All
          </button>
          <button
            onClick={() => {
              setActiveFilter("unassigned")
              onFilterChange("unassigned")
            }}
            className={`px-2 py-1 rounded-full transition-colors ${
              activeFilter === "unassigned" ? "bg-yellow-100 text-yellow-700" : "hover:bg-gray-100"
            }`}
          >
            Unassigned ({unassignedRegions.length})
          </button>
        </div>
      </div>
      {searchTerm && matchingRegions.length > 0 && (
        <div className="mt-2 p-2 bg-gray-100 rounded-md">
          <h4 className="text-sm font-medium mb-1">Matching Regions:</h4>
          <div className="flex flex-wrap gap-1">
            {matchingRegions.map((region) => (
              <button
                key={region}
                onClick={() => handleRegionClick(region)}
                className="px-2 py-1 text-xs bg-white rounded-full hover:bg-blue-100 transition-colors"
              >
                {getRegionName(region)} ({region})
              </button>
            ))}
          </div>
        </div>
      )}
      <AssignTerritoryModal
        isOpen={assignModalOpen}
        onClose={() => {
          setAssignModalOpen(false)
          setSelectedRegion(null)
        }}
        territories={territories}
        regionName={selectedRegion ? getRegionName(selectedRegion) : ""}
        onAssign={(territoryId) => {
          if (selectedRegion) {
            onAssignRegion(selectedRegion, territoryId)
            setAssignModalOpen(false)
            setSelectedRegion(null)
            // Clear the search highlights after assigning
            onSearchHighlightRegions([])
            // Update the unassigned regions
            setUnassignedRegions((prev) => prev.filter((r) => r !== selectedRegion))
          }
        }}
        onAddTerritory={(name) => {
          console.log("Add territory:", name)
        }}
      />
    </div>
  )
}

export default TerritorySearch
